import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  OPERATIONAL_QUERY_OPTIONS,
  ADMIN_QUERY_KEYS,
  invalidateAllAdminState,
  invalidateAllTLState,
} from "@/lib/api/query-options";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  GraduationCap,
  BookOpen,
  Settings,
  Layers,
  User,
  Map,
  Search,
  X,
  FilterX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CardSkeleton } from "@/components/loading";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/PaginationControls";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Swal from "sweetalert2";
import {
  fetchGroupsPaginated,
  createTeam,
  updateTeam,
  deleteTeam,
  fetchBatchesAll,
  updateTeamBatch,
  fetchActiveTeamLeaders,
  updateTeamLeaders,
  fetchLearningTracks,
  fetchTeamTrackAssignmentsAll,
  assignTeamTrack,
  removeTeamTrack,
  fetchTrackCoursesAll,
  fetchAllCourses,
  fetchTeamCourseAssignmentsAll,
  assignTeamCourse,
  removeTeamCourse,
} from "@/lib/api";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import {
  TeamLeaderMultiSelect,
  TeamLeaderList,
} from "@/components/admin/groups/TeamLeaderMultiSelect";
import { mergeLeaderOptions, leaderEmailsFor } from "@/components/admin/groups/leaders";

/**
 * Groups the flat `{ track_id, course_id }` link list served by /admin/track-courses into the
 * per-track lookup this page reads. Declared at module scope so React Query can memoise the
 * `select` by identity instead of re-reducing on every render.
 */
const toTrackCoursesMap = (
  links: { track_id?: string; course_id?: string }[],
): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  for (const link of links ?? []) {
    if (!link?.track_id || !link?.course_id) continue;
    (map[link.track_id] ??= []).push(link.course_id);
  }
  return map;
};

export const Route = createFileRoute("/_authenticated/admin/groups")({
  component: () => (
    <RequirePermission
      action="manage:teams"
      fallback={<AccessDenied message="Group management is limited to System Admins." />}
    >
      <GroupsPage />
    </RequirePermission>
  ),
});

function GroupsPage() {
  const queryClient = useQueryClient();

  // Pagination, Search & Filter State — all are applied server-side via fetchGroupsPaginated.
  const { pageNumber, pageSize, search, setSearch, setPageNumber, setPageSize, reset } =
    usePaginationState({ initialPageSize: 9 });
  const [selectedBatchId, setSelectedBatchId] = useState<string>("all");
  const [selectedLeaderId, setSelectedLeaderId] = useState<string>("all");

  // 1. Fetch Data
  const { data: groupsData, isLoading } = useQuery({
    queryKey: [
      ...ADMIN_QUERY_KEYS.groupsPaged,
      pageNumber,
      pageSize,
      search,
      selectedBatchId,
      selectedLeaderId,
    ],
    queryFn: () =>
      fetchGroupsPaginated({
        pageNumber,
        pageSize,
        search: search || undefined,
        batchId: selectedBatchId,
        leaderId: selectedLeaderId,
      }),
    placeholderData: (previousData) => previousData,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  // Memoised so the derived lookups below (leader options, filters) don't re-run on every render
  // just because `?? []` minted a fresh array.
  const allGroups = useMemo(() => groupsData?.items ?? [], [groupsData]);

  const { data: batches = [] } = useQuery({
    queryKey: ["batches"],
    queryFn: fetchBatchesAll,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  const { data: leaders = [] } = useQuery({
    queryKey: ["leaders"],
    queryFn: fetchActiveTeamLeaders,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  const { data: tracks = [] } = useQuery({
    queryKey: ["tracks-list-active"],
    queryFn: fetchLearningTracks,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  const { data: courses = [] } = useQuery({
    queryKey: ["courses-list-published"],
    queryFn: fetchAllCourses,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  const { data: teamTracks = [] } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.teamTrackAssignmentsAll,
    queryFn: fetchTeamTrackAssignmentsAll,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  const { data: teamCourses = [] } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.teamCourseAssignmentsAll,
    queryFn: fetchTeamCourseAssignmentsAll,
    retry: false,
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  // Every track's course links in ONE request. This used to loop `fetchTrackCourses(t.id)` per
  // track and await each in turn, so seven tracks meant seven serial round trips — a third of
  // the request burst that was tripping the API's rate limiter on this page. Key, fetcher and
  // options deliberately match useBatchesQueries.ts so /admin/batches and /admin/groups share one
  // cached result; the per-track grouping happens in `select` so the CACHED value stays the flat
  // link list both pages expect. Left on the global 30s staleTime rather than
  // OPERATIONAL_QUERY_OPTIONS (staleTime: 0, refetchOnWindowFocus) because track↔course wiring
  // changes rarely, and nothing invalidates this key today; a mutation needing it fresh can.
  const { data: trackCoursesMap = {} } = useQuery({
    queryKey: ["all-track-courses"],
    queryFn: fetchTrackCoursesAll,
    retry: false,
    select: toTrackCoursesMap,
  });

  // 2. Helper Derived Data
  const assignedTrackIdsMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const tt of teamTracks) {
      if (!map[tt.team_id]) map[tt.team_id] = [];
      map[tt.team_id].push(tt.track_id);
    }
    return map;
  }, [teamTracks]);

  const assignedCourseIdsMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const tc of teamCourses) {
      if (!map[tc.team_id]) map[tc.team_id] = [];
      map[tc.team_id].push(tc.course_id);
    }
    return map;
  }, [teamCourses]);

  const handleGroupMutationSuccess = async () => {
    await Promise.all([
      invalidateAllAdminState(queryClient),
      invalidateAllTLState(queryClient),
    ]);
  };

  // 3. Basic CRUD Mutations
  const createMut = useMutation({
    mutationFn: createTeam,
    onSuccess: async () => {
      toast.success("Group created successfully");
      await handleGroupMutationSuccess();
      setIsCreateOpen(false);
    },
    onError: (err: any) => toast.error(err.message || "Failed to create group"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateTeam(id, data),
    onSuccess: async () => {
      toast.success("Group updated successfully");
      await handleGroupMutationSuccess();
      setIsEditOpen(false);
    },
    onError: (err: any) => toast.error(err.message || "Failed to update group"),
  });

  const deleteMut = useMutation({
    mutationFn: deleteTeam,
    onSuccess: async () => {
      toast.success("Group deleted successfully");
      await handleGroupMutationSuccess();
    },
    onError: (err: any) => toast.error(err.message || "Failed to delete group"),
  });

  // 4. Assignment Mutations
  const updateLeadersMut = useMutation({
    mutationFn: ({ teamId, leaderIds }: { teamId: string; leaderIds: string[] }) =>
      updateTeamLeaders(teamId, leaderIds),
    onSuccess: handleGroupMutationSuccess,
  });

  const updateBatchMut = useMutation({
    mutationFn: ({ teamId, batchId }: { teamId: string; batchId: string | null }) =>
      updateTeamBatch(teamId, batchId),
    onSuccess: handleGroupMutationSuccess,
  });

  const assignTrackMut = useMutation({
    mutationFn: ({ teamId, trackId }: { teamId: string; trackId: string }) =>
      assignTeamTrack(teamId, trackId),
    onSuccess: handleGroupMutationSuccess,
  });

  const removeTrackMut = useMutation({
    mutationFn: ({ teamId, trackId }: { teamId: string; trackId: string }) =>
      removeTeamTrack(teamId, trackId),
    onSuccess: handleGroupMutationSuccess,
  });

  const assignCourseMut = useMutation({
    mutationFn: ({ teamId, courseId }: { teamId: string; courseId: string }) =>
      assignTeamCourse(teamId, courseId),
    onSuccess: handleGroupMutationSuccess,
  });

  const removeCourseMut = useMutation({
    mutationFn: ({ teamId, courseId }: { teamId: string; courseId: string }) =>
      removeTeamCourse(teamId, courseId),
    onSuccess: handleGroupMutationSuccess,
  });

  // 2. Dialog States
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const [formData, setFormData] = useState<{
    id: string;
    name: string;
    description: string;
    batchId: string;
    /** The group's complete leader set — every save replaces, so this is not a delta. */
    leaderIds: string[];
  }>({ id: "", name: "", description: "", batchId: "", leaderIds: [] });
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");

  const [assignFormData, setAssignFormData] = useState<{
    leaderIds: string[];
    batchId: string | null;
    assignedTracks: string[];
    assignedCourses: string[];
  }>({
    leaderIds: [],
    batchId: null,
    assignedTracks: [],
    assignedCourses: [],
  });

  // Leader picker options: the appointable leaders, plus any leader the group being edited already
  // has who is no longer appointable, so an unrelated edit can't quietly unassign them.
  const editingGroup = useMemo(
    () => allGroups.find((g: any) => g.id === formData.id),
    [allGroups, formData.id],
  );
  const formLeaderOptions = useMemo(
    () => mergeLeaderOptions(leaders, editingGroup?.leaders),
    [leaders, editingGroup],
  );

  const assigningGroup = useMemo(
    () => allGroups.find((g: any) => g.id === selectedGroupId),
    [allGroups, selectedGroupId],
  );
  const assignLeaderOptions = useMemo(
    () => mergeLeaderOptions(leaders, assigningGroup?.leaders),
    [leaders, assigningGroup],
  );

  const reachableCourseIds = useMemo(() => {
    const set = new Set<string>();
    for (const trackId of assignFormData.assignedTracks) {
      const courseIds = trackCoursesMap[trackId] || [];
      for (const cId of courseIds) {
        set.add(cId);
      }
    }
    return set;
  }, [assignFormData.assignedTracks, trackCoursesMap]);

  // 5. Handlers
  const handleCreateSubmit = () => {
    if (!formData.name) return toast.error("Name is required");
    if (!formData.batchId) return toast.error("Batch is required to create a group");
    let teamLeaderEmails: string[];
    try {
      teamLeaderEmails = leaderEmailsFor(formData.leaderIds, formLeaderOptions);
    } catch (err: any) {
      return toast.error(err.message);
    }
    createMut.mutate({
      name: formData.name,
      description: formData.description,
      batchId: formData.batchId,
      // The API takes leaders by email on create, since the group has no id to assign against yet.
      teamLeaderEmails,
    });
  };

  const handleEditSubmit = () => {
    if (!formData.name) return toast.error("Name is required");
    // PUT /groups/{id} requires an existing batch and REPLACES the leader set from the payload —
    // so batchId and teamLeaderEmails must both be sent on every edit. Omitting batchId sends
    // Guid.Empty and is rejected; omitting the leaders would unassign every one of them.
    if (!formData.batchId) return toast.error("Batch is required to save a group");
    let teamLeaderEmails: string[];
    try {
      teamLeaderEmails = leaderEmailsFor(formData.leaderIds, formLeaderOptions);
    } catch (err: any) {
      return toast.error(err.message);
    }
    updateMut.mutate({
      id: formData.id,
      data: {
        name: formData.name,
        description: formData.description,
        batchId: formData.batchId,
        teamLeaderEmails,
      },
    });
  };

  const openEdit = (group: any) => {
    setFormData({
      id: group.id,
      name: group.name,
      description: group.description || "",
      batchId: group.batch_id || "",
      leaderIds: group.leader_ids ?? [],
    });
    setIsEditOpen(true);
  };

  const openAssign = (group: any) => {
    setSelectedGroupId(group.id);
    setAssignFormData({
      leaderIds: group.leader_ids ?? [],
      batchId: group.batch_id || null,
      assignedTracks: teamTracks
        .filter((tt: any) => tt.team_id === group.id)
        .map((tt: any) => tt.track_id),
      assignedCourses: teamCourses
        .filter((tc: any) => tc.team_id === group.id)
        .map((tc: any) => tc.course_id),
    });
    setIsAssignOpen(true);
  };

  const saveAssignments = async () => {
    if (!selectedGroupId) return;

    try {
      // Leaders — a set, so compare as one and send the whole replacement set when it differs.
      const group = allGroups.find((g: any) => g.id === selectedGroupId);
      const currentLeaderIds: string[] = group?.leader_ids ?? [];
      const sameLeaders =
        currentLeaderIds.length === assignFormData.leaderIds.length &&
        [...currentLeaderIds].sort().join(",") === [...assignFormData.leaderIds].sort().join(",");
      if (!sameLeaders) {
        await updateLeadersMut.mutateAsync({
          teamId: selectedGroupId,
          leaderIds: assignFormData.leaderIds,
        });
      }

      // Batch
      if (group?.batch_id !== assignFormData.batchId) {
        await updateBatchMut.mutateAsync({
          teamId: selectedGroupId,
          batchId: assignFormData.batchId,
        });
      }

      // Tracks
      const currentTracks = teamTracks
        .filter((tt: any) => tt.team_id === selectedGroupId)
        .map((tt: any) => tt.track_id);
      const tracksToAdd = assignFormData.assignedTracks.filter((id) => !currentTracks.includes(id));
      const tracksToRemove = currentTracks.filter(
        (id) => !assignFormData.assignedTracks.includes(id),
      );

      for (const tId of tracksToAdd) {
        await assignTrackMut.mutateAsync({ teamId: selectedGroupId, trackId: tId });
      }
      for (const tId of tracksToRemove) {
        await removeTrackMut.mutateAsync({ teamId: selectedGroupId, trackId: tId });
      }

      // Courses
      const currentCourses = teamCourses
        .filter((tc: any) => tc.team_id === selectedGroupId)
        .map((tc: any) => tc.course_id);
      const coursesToAdd = assignFormData.assignedCourses.filter(
        (id) => !currentCourses.includes(id),
      );
      const coursesToRemove = currentCourses.filter(
        (id) => !assignFormData.assignedCourses.includes(id),
      );

      for (const cId of coursesToAdd) {
        await assignCourseMut.mutateAsync({ teamId: selectedGroupId, courseId: cId });
      }
      for (const cId of coursesToRemove) {
        await removeCourseMut.mutateAsync({ teamId: selectedGroupId, courseId: cId });
      }

      toast.success("Assignments updated successfully");
      setIsAssignOpen(false);
    } catch (error: any) {
      toast.error("Failed to update assignments: " + error.message);
    }
  };

  return (
    <div className="space-y-6 font-sans">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b pb-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-primary/10 text-primary shadow-inner">
              <Users className="h-7 w-7" />
            </div>
            Group Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            Manage your groups (teams), designate leaders, assign tracks, and view courses.
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData({ id: "", name: "", description: "", batchId: "", leaderIds: [] });
            setIsCreateOpen(true);
          }}
          className="bg-primary hover:bg-primary/95 text-primary-foreground font-semibold gap-2 rounded-xl h-10 shadow-md cursor-pointer transition-all hover:shadow-lg"
        >
          <Plus className="h-4 w-4" />
          Create Group
        </Button>
      </div>

      {/* Filters Panel */}
      <div className="bg-card border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground/90">Filter Groups</h2>
          {(search || selectedBatchId !== "all" || selectedLeaderId !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setSelectedBatchId("all");
                setSelectedLeaderId("all");
                reset();
              }}
              className="text-xs text-muted-foreground hover:text-destructive h-8 px-2.5 rounded-lg"
            >
              <FilterX className="h-3.5 w-3.5 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Text search (server-side) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Search className="h-3 w-3" /> Group Name / Description
            </label>
            <div className="relative">
              <Input
                placeholder="Search name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-sm w-full text-foreground"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Layers className="h-3 w-3" /> Filter by Batch
            </label>
            <Select
              value={selectedBatchId}
              onValueChange={(val) => {
                setSelectedBatchId(val);
                reset();
              }}
            >
              <SelectTrigger className="h-10 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-sm text-foreground w-full">
                <SelectValue placeholder="All Batches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                <SelectItem value="none">No Batch</SelectItem>
                {batches.map((b: any) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <User className="h-3 w-3" /> Filter by Team Lead
            </label>
            <Select
              value={selectedLeaderId}
              onValueChange={(val) => {
                setSelectedLeaderId(val);
                reset();
              }}
            >
              <SelectTrigger className="h-10 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-sm text-foreground w-full">
                <SelectValue placeholder="All Team Leaders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Leaders</SelectItem>
                <SelectItem value="none">No Team Leader</SelectItem>
                {leaders.map((l: any) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.full_name || l.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results summary */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <div>
            Showing <span className="font-semibold text-foreground">{allGroups.length}</span>{" "}
            of <span className="font-semibold text-foreground">{groupsData?.totalCount ?? 0}</span>{" "}
            total groups
          </div>
          {(search || selectedBatchId !== "all" || selectedLeaderId !== "all") && (
            <div className="flex items-center gap-1.5 bg-primary/5 text-primary px-2.5 py-1 rounded-md border border-primary/10">
              <Layers className="h-3 w-3" />
              <span>Filters active</span>
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <CardSkeleton count={6} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" />
      ) : allGroups.length === 0 ? (
        <div className="p-16 text-center border-dashed rounded-2xl border bg-card">
          <Users className="h-10 w-10 text-muted-foreground/35 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm font-medium">
            No groups match the filtering criteria.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allGroups.map((group: any, index: number) => {
            const groupLeaders = group.leaders ?? [];
            const batchName = batches.find((b: any) => b.id === group.batch_id)?.name;
            const groupTracks = teamTracks.filter((tt: any) => tt.team_id === group.id);
            const groupCourses = teamCourses.filter((tc: any) => tc.team_id === group.id);

            return (
              <div
                key={group.id}
                className="group/card relative p-6 bg-card border rounded-2xl shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300 flex flex-col justify-between overflow-hidden animate-in fade-in slide-in-from-bottom-4"
                style={{ animationDelay: `${index * 45}ms`, animationFillMode: "both" }}
              >
                {/* Decorative top gradient utilizing site colors (Navy to Gold Accent) */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-primary/80 to-accent opacity-85" />

                <Link
                  to="/admin/groups/$groupId"
                  params={{ groupId: group.id }}
                  className="flex-1 outline-none"
                >
                  <div className="flex justify-between items-start gap-2">
                    <h3
                      className="font-bold text-lg text-foreground tracking-tight group-hover/card:text-primary transition-colors truncate"
                      title={group.name}
                    >
                      {group.name}
                    </h3>
                    {batchName && (
                      <span className="text-[10px] bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-bold shadow-sm border border-primary/20 shrink-0">
                        {batchName}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2 leading-relaxed min-h-[40px]">
                    {group.description || "No description provided."}
                  </p>

                  <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-muted-foreground font-medium bg-muted/20 p-3 rounded-2xl border border-muted/40 group-hover/card:border-primary/20 transition-colors">
                    <div className="col-span-2 flex items-center gap-2">
                      <User className="h-4 w-4 text-primary shrink-0" />
                      <span className="truncate">
                        {groupLeaders.length > 1 ? "Leaders" : "Leader"}:{" "}
                        <TeamLeaderList leaders={groupLeaders} className="text-foreground" />
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Map className="h-4 w-4 text-accent" />
                      <span>
                        <span className="font-bold text-foreground">{groupTracks.length}</span>{" "}
                        Tracks
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-primary/70" />
                      <span>
                        <span className="font-bold text-foreground">{groupCourses.length}</span>{" "}
                        Courses
                      </span>
                    </div>
                  </div>
                </Link>

                <div className="flex items-center justify-between border-t border-muted/50 pt-3 mt-6 relative z-10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAssign(group)}
                    className="text-xs h-8 gap-1.5 rounded-xl cursor-pointer border-primary/20 hover:bg-primary/5 hover:text-primary text-primary/90 font-medium transition-colors shadow-sm"
                  >
                    <Settings className="h-3.5 w-3.5" /> Manage Assignments
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(group)}
                      className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer rounded-xl hover:bg-muted/70"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: `You won't be able to revert this! Delete group "${group.name}"?`,
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#163569",
                          cancelButtonColor: "#ef4444",
                          confirmButtonText: "Yes, delete it!",
                          customClass: {
                            popup: "rounded-2xl",
                            confirmButton: "rounded-xl font-semibold px-4 py-2 text-sm",
                            cancelButton: "rounded-xl font-semibold px-4 py-2 text-sm",
                          },
                        }).then((result) => {
                          if (result.isConfirmed) {
                            deleteMut.mutate(group.id);
                          }
                        });
                      }}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 cursor-pointer rounded-xl"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls (server-side) */}
      <PaginationControls
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalPages={groupsData?.totalPages ?? 1}
        totalCount={groupsData?.totalCount ?? 0}
        hasNextPage={groupsData?.hasNextPage ?? false}
        hasPreviousPage={groupsData?.hasPreviousPage ?? false}
        onPageChange={setPageNumber}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
      />

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Group Name</label>
              <Input
                placeholder="e.g. Front-end Devs"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Description</label>
              <Textarea
                placeholder="Optional details about this group..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground min-h-[80px]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-muted-foreground">
                Select Batch <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.batchId}
                onValueChange={(v) => setFormData({ ...formData, batchId: v })}
              >
                <SelectTrigger className="rounded-xl focus:ring-primary text-foreground">
                  <SelectValue placeholder="Select a batch..." />
                </SelectTrigger>
                <SelectContent>
                  {batches.map((b: any) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <User className="h-3 w-3" /> Team Leaders
              </label>
              <TeamLeaderMultiSelect
                options={formLeaderOptions}
                value={formData.leaderIds}
                onChange={(leaderIds) => setFormData({ ...formData, leaderIds })}
              />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Pick as many as you need — every leader has the same authority over the group. You
                can also leave this empty and assign leaders later.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={createMut.isPending}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold cursor-pointer"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Group Name</label>
              <Input
                placeholder="Group Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Description</label>
              <Textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground min-h-[80px]"
              />
            </div>
            {/* Batch is required by PUT /groups/{id} — it can't be left out of the payload, so it
                has to be editable here rather than silently resent. */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <Layers className="h-3 w-3" /> Batch <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.batchId}
                onValueChange={(v) => setFormData({ ...formData, batchId: v })}
              >
                <SelectTrigger className="rounded-xl focus:ring-primary text-foreground">
                  <SelectValue placeholder="Select a batch..." />
                </SelectTrigger>
                <SelectContent>
                  {batches.map((b: any) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <User className="h-3 w-3" /> Team Leaders
              </label>
              <TeamLeaderMultiSelect
                options={formLeaderOptions}
                value={formData.leaderIds}
                onChange={(leaderIds) => setFormData({ ...formData, leaderIds })}
              />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                This is the group's full set of leaders after saving — removing someone here
                unassigns them.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsEditOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={updateMut.isPending}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold cursor-pointer"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="sm:max-w-xl bg-card border rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Manage Group Assignments</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
            {/* Leader Assignment — a group can be led by any number of people, all equal. */}
            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4 text-primary" /> Assign Team Leaders
              </label>
              <TeamLeaderMultiSelect
                options={assignLeaderOptions}
                value={assignFormData.leaderIds}
                onChange={(leaderIds) => setAssignFormData({ ...assignFormData, leaderIds })}
                placeholder="No leader assigned"
              />
            </div>

            {/* Batch Assignment */}
            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                <Layers className="h-4 w-4 text-primary" /> Assign to Batch
              </label>
              <Select
                value={assignFormData.batchId || "none"}
                onValueChange={(v) =>
                  setAssignFormData({ ...assignFormData, batchId: v === "none" ? null : v })
                }
              >
                <SelectTrigger className="rounded-xl focus:ring-primary text-foreground">
                  <SelectValue placeholder="Select a batch..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ungrouped (No Batch)</SelectItem>
                  {batches.map((b: any) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tracks Assignment */}
            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                <GraduationCap className="h-4 w-4 text-primary" /> Assign Tracks
              </label>
              <div className="border rounded-xl p-2 max-h-[150px] overflow-y-auto bg-muted/10 space-y-1">
                {!Array.isArray(tracks) || tracks.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-2">No tracks available</p>
                ) : (
                  tracks.map((t: any) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/20 cursor-pointer"
                      onClick={async () => {
                        const isCurrentlyAssigned = assignFormData.assignedTracks.includes(t.id);
                        if (!isCurrentlyAssigned) {
                          const trackCoursesList = trackCoursesMap[t.id] || [];
                          const overlappingCourses = assignFormData.assignedCourses.filter((cId) =>
                            trackCoursesList.includes(cId),
                          );
                          if (overlappingCourses.length > 0) {
                            const courseNames = overlappingCourses
                              .map((cId) => courses.find((c: any) => c.id === cId)?.title || cId)
                              .join(", ");
                            const confirmResult = await Swal.fire({
                              title: "Consolidate redundant assignments?",
                              text: `Assigning this track will make these already-individually-assigned courses redundant: ${courseNames}. Proceed and consolidate?`,
                              icon: "question",
                              showCancelButton: true,
                              confirmButtonText: "Yes, consolidate",
                              cancelButtonText: "No, keep both",
                              confirmButtonColor: "#163569",
                              cancelButtonColor: "#ef4444",
                              customClass: {
                                popup: "rounded-2xl",
                                confirmButton: "rounded-xl text-sm font-semibold px-4 py-2",
                                cancelButton: "rounded-xl text-sm font-semibold px-4 py-2",
                              },
                            });

                            if (confirmResult.isConfirmed) {
                              const updatedCourses = assignFormData.assignedCourses.filter(
                                (cId) => !trackCoursesList.includes(cId),
                              );
                              setAssignFormData({
                                ...assignFormData,
                                assignedTracks: [...assignFormData.assignedTracks, t.id],
                                assignedCourses: updatedCourses,
                              });
                              return;
                            } else if (confirmResult.dismiss === Swal.DismissReason.cancel) {
                              setAssignFormData({
                                ...assignFormData,
                                assignedTracks: [...assignFormData.assignedTracks, t.id],
                              });
                              return;
                            } else {
                              return;
                            }
                          }
                        }

                        const newTracks = isCurrentlyAssigned
                          ? assignFormData.assignedTracks.filter((id) => id !== t.id)
                          : [...assignFormData.assignedTracks, t.id];
                        setAssignFormData({ ...assignFormData, assignedTracks: newTracks });
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={assignFormData.assignedTracks.includes(t.id)}
                        readOnly
                        className="h-4 w-4 rounded border-input text-primary accent-primary pointer-events-none"
                      />
                      <span className="text-sm text-foreground">{t.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Courses Assignment */}
            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                <BookOpen className="h-4 w-4 text-primary" /> Assign Courses
              </label>
              <div className="border rounded-xl p-2 max-h-[150px] overflow-y-auto bg-muted/10 space-y-1">
                {courses.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-2">No courses available</p>
                ) : (
                  courses.map((c: any) => {
                    const isReachableViaTrack = reachableCourseIds.has(c.id);
                    const providingTrack = isReachableViaTrack
                      ? tracks.find((t: any) => (trackCoursesMap[t.id] || []).includes(c.id))
                      : null;

                    return (
                      <div
                        key={c.id}
                        className={cn(
                          "flex items-center justify-between p-2 rounded-lg hover:bg-muted/20 cursor-pointer",
                          isReachableViaTrack && "opacity-60 cursor-not-allowed bg-muted/5",
                        )}
                        onClick={() => {
                          if (isReachableViaTrack) {
                            toast.warning(
                              `Course is already available via the "${providingTrack?.name || "assigned"}" track.`,
                            );
                            return;
                          }
                          const newCourses = assignFormData.assignedCourses.includes(c.id)
                            ? assignFormData.assignedCourses.filter((id) => id !== c.id)
                            : [...assignFormData.assignedCourses, c.id];
                          setAssignFormData({ ...assignFormData, assignedCourses: newCourses });
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={
                              assignFormData.assignedCourses.includes(c.id) || isReachableViaTrack
                            }
                            disabled={isReachableViaTrack}
                            readOnly
                            className="h-4 w-4 rounded border-input text-primary accent-primary pointer-events-none"
                          />
                          <span className="text-sm text-foreground">{c.title}</span>
                        </div>
                        {isReachableViaTrack && (
                          <span className="text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-800/30 font-medium">
                            via {providingTrack?.name || "Track"}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsAssignOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={saveAssignments}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold cursor-pointer"
            >
              Save Assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
